/**
 * Drag-to-reveal before/after comparison for image-comparison-slider section.
 */

(function () {
  'use strict';

  const MIN_PCT = 2;
  const MAX_PCT = 98;
  const KEY_STEP = 5;

  /**
   * @param {HTMLElement} root
   */
  function initComparison(root) {
    if (root.dataset.comparisonReady === 'true') {
      return;
    }

    const host = root.querySelector('[data-comparison-host]');
    const divider = root.querySelector('.image-comparison-slider__divider');
    const handle = root.querySelector('[data-comparison-handle]');

    if (!host || !divider || !handle) {
      return;
    }

    let dragging = false;

    /**
     * @param {number} clientX
     */
    function setSplitFromClientX(clientX) {
      const rect = host.getBoundingClientRect();
      if (rect.width <= 0) {
        return;
      }

      const ratio = (clientX - rect.left) / rect.width;
      const pct = Math.round(
        Math.min(MAX_PCT, Math.max(MIN_PCT, ratio * 100)),
      );
      root.style.setProperty('--split-pct', String(pct));
      handle.setAttribute('aria-valuenow', String(pct));
    }

    /**
     * @param {PointerEvent} event
     */
    function onPointerMove(event) {
      if (!dragging) {
        return;
      }
      setSplitFromClientX(event.clientX);
    }

    /**
     * @param {PointerEvent} event
     */
    function onPointerUp(event) {
      if (!dragging) {
        return;
      }
      dragging = false;
      divider.releasePointerCapture(event.pointerId);
      divider.removeEventListener('pointermove', onPointerMove);
      divider.removeEventListener('pointerup', onPointerUp);
      divider.removeEventListener('pointercancel', onPointerUp);
    }

    /**
     * @param {PointerEvent} event
     */
    function onPointerDown(event) {
      if (event.pointerType === 'mouse' && event.button !== 0) {
        return;
      }
      dragging = true;
      divider.setPointerCapture(event.pointerId);
      divider.addEventListener('pointermove', onPointerMove);
      divider.addEventListener('pointerup', onPointerUp);
      divider.addEventListener('pointercancel', onPointerUp);
      setSplitFromClientX(event.clientX);
    }

    /**
     * @param {KeyboardEvent} event
     */
    function onHandleKeydown(event) {
      const current = Number.parseInt(
        root.style.getPropertyValue('--split-pct').trim(),
        10,
      );
      const base = Number.isFinite(current) ? current : 50;
      let next = base;

      if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') {
        next = Math.max(MIN_PCT, base - KEY_STEP);
      } else if (event.key === 'ArrowRight' || event.key === 'ArrowUp') {
        next = Math.min(MAX_PCT, base + KEY_STEP);
      } else if (event.key === 'Home') {
        next = MIN_PCT;
      } else if (event.key === 'End') {
        next = MAX_PCT;
      } else {
        return;
      }

      event.preventDefault();
      root.style.setProperty('--split-pct', String(next));
      handle.setAttribute('aria-valuenow', String(next));
    }

    divider.addEventListener('pointerdown', onPointerDown);
    handle.addEventListener('keydown', onHandleKeydown);
    root.dataset.comparisonReady = 'true';
  }

  function initAll() {
    document.querySelectorAll('[data-image-comparison-root]').forEach((el) => {
      if (!(el instanceof HTMLElement)) {
        return;
      }
      initComparison(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    if (!(event.target instanceof HTMLElement)) {
      return;
    }
    event.target
      .querySelectorAll('[data-image-comparison-root]')
      .forEach((el) => {
        if (!(el instanceof HTMLElement)) {
          return;
        }
        initComparison(el);
      });
  });
})();
